import { GoogleMap, MarkerF, useJsApiLoader } from '@react-google-maps/api';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import Skeleton from 'react-loading-skeleton';

import mapPinBlue from '@/static/assets/map-pin-blue.svg';

import styles from './map-view.module.scss';

const apiKey = process.env.GOOGLE_API_KEY;

const options = {
  streetViewControl: false,
  mapTypeControl: false,
  minZoom: 6,
  maxZoom: 15,
  gestureHandling: 'greedy',
  componentRestrictions: { country: 'ca' },
  draggable: false,
  disableDefaultUI: true,
  fullscreenControl: false,
};

const MapView = (props: any) => {
  const { facilityData } = props;
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: apiKey });
  const { locale }: any = useRouter();
  const mapRef = useRef<google.maps.Map | null>(null);

  const Address = facilityData?.attributes?.Address;
  const latlngLocation = Address?.Location;

  const getLatLng = () => {
    let lngLatObj;
    if (Address?.Latitude) {
      lngLatObj = {
        lat: parseFloat(Address.Latitude),
        lng: parseFloat(Address.Longitude),
      };
    } else if (Address?.Latitude === null && Address?.Longitude === null) {
      const location = Address?.Location.split('(')[1].slice(0, -1);
      const newLocation = location.split(' ');
      lngLatObj = {
        lat: parseFloat(newLocation[1]),
        lng: parseFloat(newLocation[0]),
      };
    } else if (latlngLocation) {
      const regExp = /\(([^)]+)\)/;
      const latlngObj = regExp.exec(latlngLocation)?.[1].split(' ');
      lngLatObj = {
        lat: parseFloat(latlngObj[0]),
        lng: parseFloat(latlngObj[1]),
      };
    } else {
      lngLatObj = {
        lat: 51.0553635,
        lng: -114.0186927,
      };
    }
    return lngLatObj;
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      const expandControl = document.querySelector(
        `.${styles.customExpandMap}`
      ) as HTMLElement;
      const exitControl = document.querySelector(
        `.${styles.customExitFullscreen}`
      ) as HTMLElement;
      const mapContainer = document.querySelector(
        '.locator-google-map'
      ) as HTMLElement;
      const isFullscreen =
        !!document.fullscreenElement ||
        mapContainer?.classList.contains(styles.fullscreenMode);

      if (expandControl)
        expandControl.style.display = isFullscreen ? 'none' : 'flex';
      if (exitControl)
        exitControl.style.display = isFullscreen ? 'block' : 'none';

      document.body.style.overflow = isFullscreen ? 'hidden' : 'auto';
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.body.style.overflow = 'auto';
    };
  }, []);

  const onLoad = (map: google.maps.Map) => {
    mapRef.current = map;

    const expandControl = document.createElement('div');
    expandControl.className = styles.customExpandMap;

    const icon = document.createElement('img');
    icon.src = '/images/icons/expand-icon.svg';
    icon.alt = 'map icon';
    icon.className = styles.expandIcon;

    const text = document.createElement('span');
    text.textContent = locale === 'en' ? 'Expand Map' : 'Agrandir la carte';
    text.classList.add('st-expand-map');

    expandControl.appendChild(icon);
    expandControl.appendChild(text);

    const mapContainer = document.querySelector(
      '.locator-google-map'
    ) as HTMLElement;

    const exitControl = document.createElement('div');
    exitControl.className = styles.customExitFullscreen;

    const exitCross = document.createElement('img');
    exitCross.src = '/images/sv-modal-cross-icon.svg';
    exitCross.alt = 'Close';
    exitCross.className = styles.exitCross;

    exitControl.appendChild(exitCross);
    mapContainer.appendChild(exitControl);

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const supportsFullscreen = !!mapContainer.requestFullscreen;

    expandControl.addEventListener('click', () => {
      if (isIOS || !supportsFullscreen) {
        mapContainer.classList.add(styles.fullscreenMode);
        exitControl.style.display = 'block';
        expandControl.style.display = 'none';
        document.body.style.overflow = 'hidden';
      } else {
        mapContainer.requestFullscreen().catch((err) => {
          console.error('Fullscreen request failed:', err);
        });
      }
    });

    exitControl.addEventListener('click', () => {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      }

      if (mapContainer.classList.contains(styles.fullscreenMode)) {
        mapContainer.classList.remove(styles.fullscreenMode);
        exitControl.style.display = 'none';
        expandControl.style.display = 'flex';
        document.body.style.overflow = 'auto';
      }
    });

    map.controls[window.google.maps.ControlPosition.RIGHT_BOTTOM].push(
      expandControl
    );
  };

  return (
    <>
      {isLoaded && Address?.Location ? (
        <div className="sv-revamp-map-container position-relative">
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={getLatLng()}
            zoom={12}
            options={options}
            mapContainerClassName="locator-google-map"
            onLoad={onLoad}
          >
            <MarkerF
              position={getLatLng()}
              icon={{
                url: mapPinBlue,
                anchor: new window.google.maps.Point(55, 20),
                labelOrigin: new window.google.maps.Point(55, 15),
                scaledSize: new window.google.maps.Size(110, 40),
              }}
            />
          </GoogleMap>
        </div>
      ) : (
        <Skeleton height="180" />
      )}
    </>
  );
};

export default MapView;
